'use client';

import React, { useState, useRef } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import { DisplayConceptRef, DisplayUser, updateFollows } from '@/lib/api';

import { Modal } from './Modal';
import { TopicSelector } from './TopicSelector';

interface FollowButtonProps {
  authUsername: string;
  followUsername: string;
  currentlyFollowing: boolean;
  topics: DisplayConceptRef[];
  onFollowUpdate?: (updatedUserInfo: DisplayUser) => void;
  noTopicPrompt?: boolean;
}

export function FollowButton({
  authUsername,
  followUsername,
  currentlyFollowing,
  topics,
  onFollowUpdate,
  noTopicPrompt,
}: FollowButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<DisplayConceptRef[]>(topics);
  const modalId = useRef(`follow-modal-${Math.random().toString(36).substr(2, 9)}`);

  const handleOpenModal = () => {
    setShowModal(true);
    setSelectedTopics(topics);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTopics([]);
  };

  const handleFollowUnfollowUser = async () => {
    try {
      const updatedUserInfo: DisplayUser = await updateFollows({
        action: currentlyFollowing ? 'unfollow' : 'follow',
        username_to_follow: followUsername,
        topics: currentlyFollowing ? [] : selectedTopics.map((topic) => topic.cref),
      });
      if (onFollowUpdate) {
        onFollowUpdate(updatedUserInfo);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error following/unfollowing user:', error);
    }
  };

  const updateFollowUserUpdateTopics = async () => {
    try {
      const updatedUserInfo: DisplayUser = await updateFollows({
        action: 'update',
        username_to_follow: followUsername,
        topics: selectedTopics.map((topic) => topic.cref),
      });
      if (onFollowUpdate) {
        onFollowUpdate(updatedUserInfo);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error updating follow of user:', error);
    }
  };

  const handleTopicSelect = (topic: DisplayConceptRef | null) => {
    if (topic) {
      setSelectedTopics((prevTopics) => [...prevTopics, topic]);
    }
  };

  return (
    <>
      <Button
        onClick={noTopicPrompt ? handleFollowUnfollowUser : handleOpenModal}
        aria-haspopup={!noTopicPrompt ? 'dialog' : undefined}
        aria-expanded={showModal}
        variant={currentlyFollowing ? 'outlined' : 'contained'}
        size="small"
      >
        {currentlyFollowing ? `Following` : `Follow`}
      </Button>
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={`${currentlyFollowing ? 'unfollow' : 'follow'} ${followUsername} ${
          currentlyFollowing ? 'or update topics:' : 'on:'
        }`}
      >
        <Box sx={{ p: 2 }}>
          {selectedTopics.length === 0 ? (
            <List>
              <ListItem>
                <ListItemText primary={<Typography fontStyle="italic">All topics</Typography>} />
              </ListItem>
            </List>
          ) : (
            <Box sx={{ mb: 2 }}>
              <List>
                {selectedTopics.map((topic, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setSelectedTopics((prevTopics) =>
                            prevTopics.filter((_, i) => i !== index)
                          )
                        }
                        aria-label={`Remove topic ${topic.display}`}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={topic.display} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography component="label" htmlFor="topic-selector" sx={{ display: 'block', mb: 1 }}>
              {selectedTopics.length === 0 ? 'Or select topics:' : 'Add topic:'}
            </Typography>
            <TopicSelector
              instanceId="follow-button-topic-selector"
              onChangeFn={handleTopicSelect}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {currentlyFollowing && (
              <Button onClick={updateFollowUserUpdateTopics} variant="contained">
                Update Topics
              </Button>
            )}
            <Button
              onClick={handleFollowUnfollowUser}
              variant="contained"
              color={currentlyFollowing ? 'error' : 'primary'}
            >
              {currentlyFollowing ? 'unfollow' : 'follow'}
            </Button>
            <Button onClick={handleCloseModal} variant="outlined">
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
